<template>
  <div class="col-12">
    <div class="card-body px-0 pb-2">
      <div class="p-4">
        <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
          <div class="row g-3">
            <!-- Name Fields -->
            <div class="col-md-4">
              <label for="first_name" class="bmd-label-float">
                {{ $t("students.first_name") }}
              </label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.first_name }"
                id="first_name"
                v-model.trim="formData.first_name"
                required
                @blur="validateName('first_name')"
              />
              <div class="invalid-feedback me-2">{{ errors.first_name }}</div>
            </div>
            <div class="col-md-4">
              <label for="father_name" class="bmd-label-float">
                {{ $t("students.father_name") }}
              </label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.father_name }"
                id="father_name"
                v-model.trim="formData.father_name"
                required
                @blur="validateName('father_name')"
              />
              <div class="invalid-feedback">{{ errors.father_name }}</div>
            </div>
            <div class="col-md-4">
              <label for="last_name" class="bmd-label-float">
                {{ $t("students.last_name") }}
              </label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.last_name }"
                id="last_name"
                v-model.trim="formData.last_name"
                required
                @blur="validateName('last_name')"
              />
              <div class="invalid-feedback">{{ errors.last_name }}</div>
            </div>

            <!-- Mother and Birthdate -->
            <div class="col-md-6">
              <label for="mother_name" class="bmd-label-float">
                {{ $t("students.mother_name") }}
              </label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.mother_name }"
                id="mother_name"
                v-model.trim="formData.mother_name"
                required
                @blur="validateName('mother_name')"
              />
              <div class="invalid-feedback">{{ errors.mother_name }}</div>
            </div>
            <div class="col-md-6">
              <label for="birthdate" class="bmd-label-float">
                {{ $t("students.birthdate") }}
              </label>
              <input
                type="date"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.birthdate }"
                id="birthdate"
                v-model="formData.birthdate"
                required
                @blur="validateBirthdate()"
                :max="maxBirthdate"
              />
              <div class="invalid-feedback">{{ errors.birthdate }}</div>
            </div>

            <!-- Academic Year Field -->
            <div class="col-md-6">
              <label for="current_academic_year_id" class="bmd-label-float">
                {{ $t("students.academic_year") }}
              </label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.current_academic_year_id }"
                id="current_academic_year_id"
                v-model="formData.current_academic_year_id"
                :required="!isEditMode"
              >
                <option value="">
                  {{ $t("students.select_academic_year") }}
                </option>
                <option v-if="isAcademicYearsLoading" disabled>
                  {{ $t("students.loading_academic_years") }}
                </option>
                <option
                  v-else
                  v-for="year in academicYears"
                  :key="year.id"
                  :value="year.id"
                  :selected="year.is_active"
                >
                  {{ year.name }} ({{ year.year }})
                  <span v-if="year.is_active">
                    - {{ $t("common.current") }}</span
                  >
                </option>
              </select>
              <div class="invalid-feedback">
                {{ errors.current_academic_year_id }}
              </div>
              <small
                v-if="!formData.current_academic_year_id && defaultAcademicYear"
                class="text-muted"
              >
                {{ $t("students.default_to_current_year") }}:
                {{ defaultAcademicYear.name }}
              </small>
            </div>
            <!-- Gender and Nationality and National Number and Passport -->
            <div class="col-md-6">
              <label for="gender" class="bmd-label-float">
                {{ $t("students.gender") }}
              </label>
              <select
                class="form-select borderless-input"
                id="gender"
                v-model="formData.gender"
                required
              >
                <option value="M">{{ $t("students.male") }}</option>
                <option value="F">{{ $t("students.female") }}</option>
              </select>
            </div>
            <div class="col-md-6">
              <label for="nationality_id" class="bmd-label-float">
                {{ $t("students.nationality") }}
              </label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.nationality_id }"
                id="nationality_id"
                v-model="formData.nationality_id"
                @change="handleNationalityChange"
              >
                <option value="">
                  {{ $t("students.select_nationality") }}
                </option>
                <option v-if="isNationalitiesLoading" disabled>
                  {{ $t("students.loading_nationalities") }}
                </option>
                <option
                  v-else
                  v-for="nationality in nationalities"
                  :key="nationality.id"
                  :value="nationality.id"
                >
                  {{ localizedNatName(nationality) }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.nationality_id }}</div>
            </div>

            <div class="col-md-6">
              <label for="national_num" class="bmd-label-float">
                {{ $t("students.national_num") }}
                <span v-if="isLibyan" class="text-danger">*</span>
                <span v-else class="text-muted">
                  {{ $t("students.for_libyans_only") }}
                </span>
              </label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.national_num }"
                id="national_num"
                v-model.trim="formData.national_num"
                :required="isLibyan"
                :disabled="!isLibyan"
                @blur="validateNationalNumber()"
                :placeholder="
                  isLibyan
                    ? $t('students.required_for_libyans')
                    : $t('students.for_libyans_only')
                "
              />
              <div class="invalid-feedback">{{ errors.national_num }}</div>
            </div>

            <div class="col-md-6">
              <label for="passport_num" class="bmd-label-float">
                {{ $t("students.passport_num") }}
                <span v-if="!isLibyan" class="text-danger">*</span>
                <span v-else class="text-muted">
                  {{ $t("students.for_non_libyans_only") }}
                </span>
              </label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.passport_num }"
                id="passport_num"
                v-model.trim="formData.passport_num"
                :required="!isLibyan && !isEditMode"
                :disabled="isLibyan"
                @blur="validatePassport()"
                :placeholder="
                  !isLibyan
                    ? $t('students.required_for_non_libyans')
                    : $t('students.for_non_libyans_only')
                "
              />
              <div class="invalid-feedback">{{ errors.passport_num }}</div>
            </div>
            <!-- Address -->
            <div class="col-12">
              <label for="address" class="bmd-label-float">
                {{ $t("students.address") }}
              </label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.address }"
                id="address"
                v-model.trim="formData.address"
                required
                @blur="validateAddress()"
              />
              <div class="invalid-feedback">{{ errors.address }}</div>
            </div>

            <!-- Class and Group -->
            <div class="col-md-6">
              <div class="form-group">
                <label for="class_id" class="bmd-label-float">
                  {{ $t("students.class") }}
                </label>
                <select
                  v-model="formData.class_id"
                  class="form-select borderless-input"
                  :class="{ 'is-invalid': errors.class_id }"
                  id="class_id"
                  required
                  :disabled="classFieldsLocked"
                  @change="fetchGroupsForClass"
                  @blur="validateClass()"
                >
                  <option value="">{{ $t("students.select_class") }}</option>
                  <option v-if="isClassesLoading" disabled>
                    {{ $t("students.loading_classes") }}
                  </option>
                  <option v-else-if="classes.length === 0" disabled>
                    {{ $t("students.no_classes") }}
                  </option>
                  <option
                    v-else
                    v-for="classItem in classes"
                    :key="classItem.id"
                    :value="classItem.id"
                  >
                    {{ classItem.name }}
                  </option>
                </select>
                <div class="invalid-feedback">{{ errors.class_id }}</div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="group_id" class="bmd-label-float">
                  {{ $t("students.group") }}
                </label>
                <select
                  v-model="formData.group_id"
                  class="form-select borderless-input"
                  :class="{ 'is-invalid': errors.group_id }"
                  id="group_id"
                  required
                  :disabled="classFieldsLocked || !formData.class_id || isGroupsLoading"
                  @blur="validateGroup()"
                >
                  <option value="">{{ $t("students.select_group") }}</option>
                  <option v-if="isGroupsLoading" disabled>
                    {{ $t("students.loading_groups") }}
                  </option>
                  <option v-else-if="groups.length === 0" disabled>
                    {{ $t("students.no_groups") }}
                  </option>
                  <option
                    v-else
                    v-for="group in groups"
                    :key="group.id"
                    :value="group.id"
                  >
                    {{ group.name }}
                  </option>
                </select>
                <div class="invalid-feedback">{{ errors.group_id }}</div>
                <div v-if="classFieldsLocked" class="text-muted small mt-1">
                  {{ $t("students.class_locked_hint") }}
                  <router-link :to="{ name: 'academic.promotion' }">
                    {{ $t("students.class_locked_link") }}
                  </router-link>
                </div>
              </div>
            </div>
            <!-- register date -->
            <div class="col-md-6">
              <label for="register_date" class="bmd-label-float">
                {{ $t("students.register_date") }}
              </label>
              <input
                type="date"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.register_date }"
                id="register_date"
                v-model="formData.register_date"
                required
                @blur="validateRegisterDate()"
                :max="maxDate"
              />
              <div class="invalid-feedback">{{ errors.register_date }}</div>
            </div>
            <!-- Chronic Disease -->
            <div class="col-md-6">
              <label for="chronic_disease" class="bmd-label-float">
                {{ $t("students.chronic_disease") }}
              </label>
              <textarea
                type="textarea"
                class="form-control outlined-input"
                id="chronic_disease"
                v-model.trim="formData.chronic_disease"
                rows="3"
              ></textarea>
            </div>

            <div class="col-md-12">
              <label for="dietary_restrictions" class="bmd-label-float">
                {{ $t("students.dietary_restrictions") }}
              </label>
              <textarea
                class="form-control outlined-input"
                id="dietary_restrictions"
                v-model.trim="formData.dietary_restrictions"
                rows="2"
                :placeholder="$t('students.dietary_placeholder')"
              ></textarea>
              <small class="text-muted">{{
                $t("students.dietary_hint")
              }}</small>
            </div>
            <!-- Contact Information -->
            <div class="col-md-6">
              <label for="email" class="bmd-label-float">
                {{ $t("students.email") }}
                <small class="text-muted"
                  >— {{ $t("students.email_hint") }}</small
                >
              </label>
              <input
                type="email"
                class="form-control outlined-input"
                :class="{ 'is-invalid': errors.email }"
                id="email"
                v-model.trim="formData.email"
                @blur="validateEmail()"
              />
              <div class="invalid-feedback">{{ errors.email }}</div>
            </div>
            <div class="col-md-6">
              <label for="phone" class="bmd-label-float">
                {{ $t("students.phone") }}
              </label>
              <input
                type="tel"
                class="form-control outlined-input"
                :class="{ 'is-invalid': errors.phone }"
                id="phone"
                v-model.trim="formData.phone"
                @blur="validatePhone()"
              />
              <div class="invalid-feedback">{{ errors.phone }}</div>
            </div>

            <!-- Password and Image -->
            <div class="col-md-6">
              <label for="password" class="bmd-label-float">
                {{ $t("students.password") }}
                <small class="text-muted"
                  >— {{ $t("students.email_hint") }}</small
                >
              </label>
              <input
                type="password"
                class="form-control outlined-input"
                :class="{ 'is-invalid': errors.password }"
                id="password"
                v-model="formData.password"
                :required="false"
                @blur="validatePassword()"
              />
              <div class="invalid-feedback">{{ errors.password }}</div>
              <small v-if="isEditMode" class="text-muted">
                {{ $t("students.password_leave_blank") }}
              </small>
            </div>
            <div class="col-md-6">
              <label for="confirm_password" class="bmd-label-float">
                {{ $t("students.confirm_password") }}
              </label>
              <input
                type="password"
                class="form-control outlined-input"
                :class="{ 'is-invalid': errors.confirm_password }"
                id="confirm_password"
                v-model="formData.confirm_password"
                :required="!!formData.password"
                @blur="validateConfirmPassword()"
              />
              <div class="invalid-feedback">{{ errors.confirm_password }}</div>
            </div>
            <div class="col-md-12">
              <label for="image" class="f">
                {{ $t("students.image") }}
              </label>
              <div
                class="card dashed-border p-3 text-center cursor-pointer"
                @click="$refs.fileInput.click()"
              >
                <input
                  type="file"
                  ref="fileInput"
                  id="image"
                  accept="image/*"
                  style="display: none"
                  @change="handleImageUpload"
                />

                <div v-if="loadingImage" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>

                <img
                  v-else-if="previewImage"
                  :src="previewImage"
                  class="img-fluid mx-auto d-block"
                  style="max-height: 150px"
                />

                <div v-else class="py-4">
                  <i class="bi bi-image fs-1 text-muted"></i>
                  <p class="text-muted mt-2 mb-0">
                    {{ $t("students.click_to_upload") }}
                  </p>
                </div>

                <button
                  v-if="previewImage"
                  @click.stop="clearImage"
                  class="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>

              <div v-if="errors.image" class="invalid-feedback d-block">
                <div v-for="(error, index) in errors.image" :key="index">
                  {{ error }}
                </div>
              </div>
              <small class="text-muted d-block mt-1">
                {{ $t("students.image_hint") }}
              </small>
            </div>
          </div>

          <div class="d-flex gap-3 mt-3">
            <button
              type="submit"
              class="btn btn-primary btn-sm flex-grow-1"
              :disabled="loading"
            >
              <span v-if="loading">
                <i class="fas fa-spinner fa-spin ms-2"></i>
                {{ $t("students.saving") }}
              </span>
              <span v-else>
                <i class="fas fa-save ms-2"></i>
                {{ submitText || $t("students.save_changes") }}
              </span>
            </button>
            <router-link
              v-if="cancelRoute"
              :to="cancelRoute"
              class="btn btn-outline-secondary btn-sm flex-grow-1"
              :disabled="loading"
            >
              {{ $t("students.cancel") }}
              <i class="fas fa-times ms-2"></i>
            </router-link>
            <button
              v-else
              type="button"
              class="btn btn-outline-secondary flex-grow-1"
              @click="handleCancel"
              :disabled="loading"
            >
              <i class="fas fa-times ms-2"></i>
              {{ $t("students.cancel") }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script setup>
import {
  ref,
  computed,
  defineProps,
  defineEmits,
  onMounted,
  watch,
  onBeforeUnmount,
} from "vue";
import { useRouter } from "vue-router";
import api from "@/api";
import { useToast } from "vue-toastification";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();
const route = useRoute();
const toast = useToast();
const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  submitText: {
    type: String,
    default: "حفظ",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showBackButton: {
    type: Boolean,
    default: true,
  },
  backRoute: {
    type: [String, Object],
    default: "/",
  },
  cancelRoute: {
    type: [String, Object],
    default: null,
  },
  studentId: {
    type: [String, Number],
    default: null,
  },
  initialData: {
    type: Object,
    default: () => ({
      id: null,
      first_name: "",
      father_name: "",
      last_name: "",
      mother_name: "",
      birthdate: "",
      gender: "M",
      national_num: "",
      address: "",
      password: "",
      nationality_id: "",
      register_date: new Date().toISOString().split("T")[0],
      class_id: null,
      group_id: null,
      image: null,
      image_url: null,
      passport_num: null,
      chronic_disease: null,
      email: null,
      phone: null,
    }),
  },
  classes: {
    type: Array,
    default: () => [],
  },
  isEditMode: {
    type: Boolean,
    default: false,
  },
});
const academicYears = ref([]);
const isAcademicYearsLoading = ref(false);
const defaultAcademicYear = ref(null);

const fetchAcademicYears = async () => {
  isAcademicYearsLoading.value = true;
  try {
    const response = await api.get("/academic-years");
    academicYears.value = response.data.data;

    // Find default (active) academic year
    defaultAcademicYear.value = academicYears.value.find(
      (year) => year.is_active
    );

    // Set default academic year if not in edit mode
    if (!props.isEditMode && defaultAcademicYear.value) {
      formData.value.current_academic_year_id = defaultAcademicYear.value.id;
    }
  } catch (error) {
    console.error("Error fetching academic years:", error);
    toast.error(t("studentForm.toasts.loadAcademicYearsFailed"));
  } finally {
    isAcademicYearsLoading.value = false;
  }
};

const validateAcademicYear = () => {
  if (!formData.value.current_academic_year_id && !props.isEditMode) {
    errors.value.current_academic_year_id = t(
      "studentForm.validation.academicYearRequired"
    );
    return false;
  }
  delete errors.value.current_academic_year_id;
  return true;
};
const localizedNatName = (nat) => {
  if (!nat) return "";
  const l = (locale.value || "en").split("-")[0]; // ar-YE → ar
  return nat.translations?.[l] ?? nat.translations?.en ?? nat.name ?? "";
};

const emit = defineEmits(["submit", "cancel"]);
const classes = ref([]);
const isClassesLoading = ref(false);
const router = useRouter();
const formData = ref({
  ...props.initialData,
  nationality_id:
    props.initialData.nationality_id || props.initialData.nationality?.id || "",
  register_date:
    props.initialData.register_date || new Date().toISOString().split("T")[0],
  confirm_password: "",
});
const groups = ref([]);
const isGroupsLoading = ref(false);
// Once a student has any payment record, the backend refuses to change their
// class from this form (see StudentController::update) — mirror that here so
// the fields read as locked instead of failing after a submit attempt.
const studentHasPaymentHistory = ref(false);
const classFieldsLocked = computed(
  () => props.isEditMode && studentHasPaymentHistory.value
);
const errors = ref({});
const fileInput = ref(null);
const loadingImage = ref(false);
const previewImage = ref(props.initialData.image_url || null);
const loading = ref(false);
const nationalities = ref([]);
const isLibyan = computed(() => {
  if (!formData.value.nationality_id || nationalities.value.length === 0)
    return false;
  const selectedNationality = nationalities.value.find(
    (n) => n.id.toString() === formData.value.nationality_id.toString()
  );
  return selectedNationality?.code === "LY";
});
const isNationalitiesLoading = ref(false);

const maxBirthdate = computed(() => {
  const today = new Date();
  return today.toISOString().split("T")[0];
});

const maxDate = computed(() => {
  return new Date().toISOString().split("T")[0];
});

// Validation functions
const validateName = (field) => {
  if (!formData.value[field]) {
    errors.value[field] = t("studentForm.validation.required");
    return false;
  }
  delete errors.value[field];
  return true;
};
const validateRegisterDate = () => {
  if (!formData.value.register_date) {
    errors.value.register_date = t("studentForm.validation.required");
    return false;
  }
  const registerDate = new Date(formData.value.register_date);
  const today = new Date();
  if (registerDate > today) {
    errors.value.register_date = t("studentForm.validation.registerDateFuture");
    return false;
  }
  delete errors.value.register_date;
  return true;
};
const validateBirthdate = () => {
  if (!formData.value.birthdate) {
    errors.value.birthdate = t("studentForm.validation.required");
    return false;
  }
  const birthDate = new Date(formData.value.birthdate);
  const today = new Date();
  if (birthDate >= today) {
    errors.value.birthdate = t("studentForm.validation.birthdatePast");
    return false;
  }
  delete errors.value.birthdate;
  return true;
};

const validateNationalNumber = () => {
  if (isLibyan.value && !formData.value.national_num) {
    errors.value.national_num = t(
      "studentForm.validation.libyanNationalRequired"
    );
    return false;
  }
  if (isLibyan.value && !/^\d{12}$/.test(formData.value.national_num)) {
    errors.value.national_num = t("studentForm.validation.nationalNumFormat");
    return false;
  }
  delete errors.value.national_num;
  return true;
};
const validateAddress = () => {
  if (!formData.value.address) {
    errors.value.address = t("studentForm.validation.required");
    return false;
  }
  delete errors.value.address;
  return true;
};

const validateClass = () => {
  if (!formData.value.class_id) {
    errors.value.class_id = t("studentForm.validation.required");
    return false;
  }
  delete errors.value.class_id;
  return true;
};

const validateGroup = () => {
  if (!formData.value.group_id) {
    errors.value.group_id = t("studentForm.validation.required");
    return false;
  }
  delete errors.value.group_id;
  return true;
};

const validatePassport = () => {
  if (!isLibyan.value && (formData.value.passport_num || props.isEditMode)) {
    if (!formData.value.passport_num) {
      errors.value.passport_num = t(
        "studentForm.validation.passportRequiredNonLibyan"
      );
      return false;
    }
    if (!/^[a-zA-Z0-9]{6,20}$/.test(formData.value.passport_num)) {
      errors.value.passport_num = t("studentForm.validation.passportFormat");
      return false;
    }
  }
  delete errors.value.passport_num;
  return true;
};
const validateEmail = () => {
  if (
    formData.value.email &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)
  ) {
    errors.value.email = t("studentForm.validation.emailInvalid");
    return false;
  }
  if (formData.value.email === "") {
    formData.value.email = null;
  }
  delete errors.value.email;
  return true;
};

const validatePhone = () => {
  if (formData.value.phone && !/^[\d\s+-]{7,15}$/.test(formData.value.phone)) {
    errors.value.phone = t("studentForm.validation.phoneInvalid");
    return false;
  }
  delete errors.value.phone;
  return true;
};

const validatePassword = () => {
  if (!formData.value.password) {
    delete errors.value.password;
    return true;
  }
  if (formData.value.password.length < 8) {
    errors.value.password = t("studentForm.validation.passwordMin");
    return false;
  }
  delete errors.value.password;
  return true;
};

const validateConfirmPassword = () => {
  if (!formData.value.password && !formData.value.confirm_password) {
    delete errors.value.confirm_password;
    return true;
  }
  if (formData.value.password !== formData.value.confirm_password) {
    errors.value.confirm_password = t(
      "studentForm.validation.passwordMismatch"
    );
    return false;
  }
  delete errors.value.confirm_password;
  return true;
};

const validateImage = () => {
  if (!formData.value.image) {
    delete errors.value.image;
    return true;
  }
  const file = formData.value.image;
  if (!file || !file.type) {
    errors.value.image = [t("studentForm.validation.imageUnknownType")];
    return false;
  }
  const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
  if (!validTypes.includes(file.type.toLowerCase())) {
    errors.value.image = [t("studentForm.validation.imageTypes")];
    return false;
  }
  if (file.size > 2 * 1024 * 1024) {
    errors.value.image = [t("studentForm.validation.imageMax2mb")];
    return false;
  }
  delete errors.value.image;
  return true;
};

const fetchClasses = async () => {
  isClassesLoading.value = true;
  try {
    const currentClassId = formData.value.class_id;
    const currentGroupId = formData.value.group_id;
const response = await api.get("/classes", {
  params: {
    per_page: 1000,
  },
});    classes.value = response.data.data;
    if (props.isEditMode && currentClassId) {
      const classExists = classes.value.some((c) => c.id === currentClassId);
      if (classExists) {
        formData.value.class_id = currentClassId;
        if (currentGroupId) {
          const groupExists = groups.value.some((g) => g.id === currentGroupId);
          if (groupExists) {
            formData.value.group_id = currentGroupId;
          }
        }
      }
    }
  } catch (error) {
    console.error("Error fetching classes:", error);
    toast.error(t("studentForm.toasts.loadClassesFailed"));
    classes.value = [];
  } finally {
    isClassesLoading.value = false;
  }
};

const fetchGroupsForClass = async () => {
  if (!formData.value.class_id) {
    groups.value = [];
    return;
  }
  isGroupsLoading.value = true;
  try {
    const currentGroupId = formData.value.group_id;

    const resp = await api.get(`/classes/${formData.value.class_id}/groups`);
    const payload = resp?.data?.data;
    const items = Array.isArray(payload) ? payload : payload?.data || [];

    let normalized = items.map((item) => {
      if (item?.group) {
        return { id: item.group.id, name: item.group.name };
      }
      return { id: item.id, name: item.name };
    });

    if (!normalized.length) {
      const simple = await api.get(
        `/classes/${formData.value.class_id}/groups`
      );
      const simplePayload = simple?.data?.data;
      const simpleItems = Array.isArray(simplePayload)
        ? simplePayload
        : simplePayload?.data || [];
      normalized = simpleItems.map((g) => ({ id: g.id, name: g.name }));
    }

    groups.value = normalized;

    if (props.isEditMode && currentGroupId) {
      const exists = groups.value.some((g) => g.id === currentGroupId);
      formData.value.group_id = exists
        ? currentGroupId
        : groups.value[0]?.id ?? null;
    }
  } catch (error) {
    console.error("Error fetching groups:", error);
    toast.error(t("studentForm.toasts.loadGroupsFailed"));
    groups.value = [];
  } finally {
    isGroupsLoading.value = false;
  }
};

const resetFileInput = () => {
  formData.value.image = null;
  previewImage.value = props.initialData.image_url || null;
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};
const fetchNationalities = async () => {
  isNationalitiesLoading.value = true;
  try {
    const response = await api.get("/nationalities", {
      params: { locale: (locale.value || "ar").split("-")[0] },
    });
    nationalities.value = response.data.data;
  } catch (e) {
    console.error("Error fetching nationalities:", e);
    nationalities.value = [];
  } finally {
    isNationalitiesLoading.value = false;
  }
};
const handleNationalityChange = () => {
  if (!isLibyan.value) {
    formData.value.national_num = "";
  } else {
    formData.value.passport_num = "";
  }
};
const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (!file) {
    resetFileInput();
    return;
  }
  const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
  if (
    !validTypes.includes(file.type.toLowerCase()) ||
    file.size > 2 * 1024 * 1024
  ) {
    errors.value.image = [t("studentForm.validation.imageInvalidTypeOrSize")];
    resetFileInput();
    return;
  }
  formData.value.image = file;
  delete errors.value.image;
  const reader = new FileReader();
  reader.onload = (e) => {
    previewImage.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const clearImage = () => {
  formData.value.image = null;
  previewImage.value = null;
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};
const validateForm = () => {
  errors.value = {};
  const requiredFields = [
    validateName("first_name"),
    validateName("father_name"),
    validateName("last_name"),
    validateBirthdate(),
    validateNationalNumber(),
    validateAddress(),
    validateClass(),
    validateGroup(),
    validateRegisterDate(),
    validateAcademicYear(),
  ];
  if (formData.value.mother_name || !props.isEditMode) {
    requiredFields.push(validateName("mother_name"));
  }
  if (formData.value.password || formData.value.confirm_password) {
    requiredFields.push(validatePassword());
    requiredFields.push(validateConfirmPassword());
  }
  validatePassport();
  validateEmail();
  validatePhone();
  validateImage();
  return requiredFields.every((valid) => valid);
};

const handleSubmit = async () => {
  if (!validateForm()) {
    toast.warning(t("studentForm.validation.fixErrors"));
    return;
  }
  try {
    loading.value = true;
    const formDataToSend = new FormData();
    const fieldsToSend = [
      "first_name",
      "father_name",
      "last_name",
      "mother_name",
      "birthdate",
      "gender",
      "national_num",
      "address",
      "nationality_id",
      "register_date",
      "class_id",
      "group_id",
      "passport_num",
      "chronic_disease",
      "email",
      "phone",
       "current_academic_year_id" 
    ];
    fieldsToSend.forEach((field) => {
      if (
        formData.value[field] !== undefined &&
        formData.value[field] !== null
      ) {
        formDataToSend.append(field, formData.value[field]);
      }
    });
    if (formData.value.password) {
      formDataToSend.append("password", formData.value.password);
    }
    if (formData.value.image instanceof File) {
      formDataToSend.append("image", formData.value.image);
    } else if (!formData.value.image && !previewImage.value) {
      formDataToSend.append("remove_image", "1");
    }
    const endpoint = props.isEditMode
      ? `/students/${props.initialData.id}`
      : "/students";
    props.isEditMode ? "PUT" : "POST";
    if (props.isEditMode) {
      formDataToSend.append("_method", "PUT");
    }
    await api.post(endpoint, formDataToSend, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    toast.success(
      props.isEditMode
        ? t("studentForm.toasts.updateSuccess")
        : t("studentForm.toasts.createSuccess")
    );
    router.push("/students");
  } catch (error) {
    console.error("Submission failed:", error);
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors || {};
      toast.error(t("studentForm.validation.fixErrors"));
    } else {
      toast.error(
        error.response?.data?.message ||
          (props.isEditMode
            ? t("studentForm.toasts.updateFail")
            : t("studentForm.toasts.createFail"))
      );
    }
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  emit("cancel");
};

onMounted(async () => {
  await fetchNationalities();
  await fetchAcademicYears();
  await fetchClasses();
  if (props.isEditMode && props.studentId) {
    await fetchStudentData();
  }
});
watch(
  () => locale.value,
  () => fetchNationalities()
);

watch(
  () => formData.value.class_id,
  (newClassId) => {
    if (newClassId) {
      fetchGroupsForClass();
    } else {
      groups.value = [];
      formData.value.group_id = null;
    }
  }
);
watch(
  () => route.params.id,
  async (newId) => {
    if (newId && props.isEditMode) {
      await fetchStudentData();
    }
  }
);
watch(
  () => props.initialData,
  (newData) => {
    if (props.isEditMode && newData) {
      formData.value = {
        ...newData,
        password: "",
        confirm_password: "",
        image: formData.value.image,
        image_url: newData.image_url || formData.value.image_url,
      };
      previewImage.value = newData.image_url || null;
      fetchClasses().then(() => {
        if (newData.class_id) {
          fetchGroupsForClass();
        }
      });
    }
  },
  { immediate: true, deep: true }
);
const loadingStudentData = ref(false);
const fetchStudentData = async () => {
  try {
    loadingStudentData.value = true;
    const response = await api.get(`/students/${props.studentId}`);
    const student = response.data.data;
    formData.value = {
      ...formData.value,
      ...student,
      password: "",
      confirm_password: "",
      birthdate: formatDateForInput(student.birthdate),
      register_date: formatDateForInput(student.register_date),
      class_id: student.current_assignment?.class?.id || null,
      group_id: student.current_assignment?.group?.id || null,
      image: formData.value.image,
      image_url: student.image_url || formData.value.image_url,
      nationality_id: student.nationality_id || student.nationality?.id || "",
    };
    studentHasPaymentHistory.value = !!student.has_payment_history;
  } catch (error) {
    console.error("Error loading student data:", error);
    toast.error(t("studentForm.toasts.loadStudentFailed"));
  } finally {
    loadingStudentData.value = false;
  }
};

const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};
onBeforeUnmount(() => {
  localStorage.removeItem("editStudentData");
});
</script>
